<template>
  <SelectRoot
    v-model="computedModel"
  >
    <SelectTrigger
      class="months-picker__trigger"
      aria-label="Customize options"
      :class="[
        borderless && 'borderless',
        { 'pl-6 pr-3 pb-3.5 pt-6 h-16': size === 'xl' },
        { 'pl-5 pr-2 pb-2 pt-3.5 h-12': size === 'base' }
      ]"
      :disabled="disabled"
      v-bind="$attrs"
    >
      <label
        v-if="!!label"
        class="select-none block text-brand-black/50 absolute !text-base flex-grow z-0 top-2 !leading-none"
      >
        {{ label }}
      </label>
      <SelectValue placeholder="" class="text-brand-black" />

      <ChevronDownIcon
        v-if="!loading"
        class="size-4 mb-1"
        :class="disabled ? 'text-brand-black/20' : 'text-brand-black/50'"
      />
      <LoadingSpinner v-if="loading" class="h-5 w-5" />
    </SelectTrigger>

    <SelectPortal>
      <SelectContent
        class="min-w-[90vw] lg:min-w-[20rem] bg-brand-white shadow-xl rounded will-change-[opacity,transform] data-[side=top]:animate-slideDownAndFade data-[side=right]:animate-slideLeftAndFade data-[side=bottom]:animate-slideUpAndFade data-[side=left]:animate-slideRightAndFade z-[100] mt-1"
        position="popper"
        :body-lock="false"
      >
        <SelectScrollUpButton class="flex items-center justify-center h-6 bg-brand-black/10 cursor-default">
          <ChevronDownIcon class="h-3.5 w-3.5 rotate-180" />
        </SelectScrollUpButton>

        <SelectViewport class="p-1">
          <SelectItem
            v-for="(option) in options"
            :key="option"
            class="text-sm leading-none text-brand-black/90 min-h-[rem] flex items-center px-6 py-2 rounded-sm relative cursor-pointer select-none data-[disabled]:text-brand-black/20 data-[disabled]:pointer-events-none data-[highlighted]:outline-none data-[highlighted]:bg-brand-primary/10 data-[highlighted]:text-brand-black"
            :value="option"
            :disabled="disabled"
          >
            <SelectItemIndicator class="absolute left-0 w-[25px] inline-flex items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="32"
                viewBox="0 0 24 24"
              ><path fill="currentColor" d="m10 17l5-5l-5-5v10z" /></svg>
            </SelectItemIndicator>
            <SelectItemText>
              <slot name="item" v-bind="{ text: formatMonths(option), value: option }">
                {{ formatMonths(option) }}
              </slot>
            </SelectItemText>
          </SelectItem>
        </SelectViewport>

        <SelectScrollDownButton class="flex items-center justify-center h-6 bg-brand-black/10 text-brand-black/90 cursor-default">
          <ChevronDownIcon class="h-3.5 w-3.5" />
        </SelectScrollDownButton>
      </SelectContent>
    </SelectPortal>
  </SelectRoot>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import ChevronDownIcon from '~/components/icons/ChevronDownIcon.vue';
import LoadingSpinner from '~/components/icons/LoadingSpinner.vue';
import {
  SelectContent,
  SelectItem,
  SelectItemIndicator,
  SelectItemText,
  SelectPortal,
  SelectRoot,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectTrigger,
  SelectValue,
  SelectViewport,
} from 'radix-vue';
import { useApi } from '~/composables/useApi';
import type { InputSizes } from '~/types';
import { useI18n } from '#imports';

const { t } = useI18n();

interface Props {
  modelValue?: string
  label?: string
  months?: any
  size?: InputSizes
  autofill?: boolean
  disabled?: boolean
  borderless?: boolean
}

function formatMonths (month: number) {
  return `${(month)} ${t('month', Number(month))}`;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  months: Array.from({ length: 12 }, (_, idx) => String(idx + 1)),
  size: 'xl',
  label: 'Duración',
  disabled: false,
  borderless: false,
});

const emit = defineEmits(['loaded']);
const computedModel = defineModel<Props['modelValue']>();
const loading = ref(false);
const { getAvailableSubscriptionMonths } = useApi();
const options = ref(props.months);

if (props.autofill) {
  loading.value = true;
  getAvailableSubscriptionMonths()
    .then((response) => {
      const results = response.data?.results;
      if (results.length > 0) {
        options.value = response.data?.results
          .toSorted((a, b) => b - a)
          .map(month => (String(month))) || [];

        if (props.modelValue) {
          computedModel.value = props.modelValue;
          return;
        }
        computedModel.value = computedModel.value
          ? options.value.find((v: string) => computedModel.value === v)
          : options.value.at(0);
      }
      emit('loaded');
    }).catch((error) => {
      console.error('Error fetching available subscription months', error);
    }).finally(() => {
      loading.value = false;
    });
}
if (!props.autofill && props.months.length) {
  options.value = props.months;
}
</script>

<style scoped>
.months-picker__trigger {
  @apply inline-flex justify-between data-[disabled]:text-brand-black/20 data-[placeholder]:text-brand-black/90 relative;
  @apply items-center border-brand-secondary/20 relative focus-within:ring-2 focus:outline-none bg-white;
  &:not(.borderless) {
    @apply border shadow-lg rounded-md;
  }
}
</style>
